import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Users,
  MessageSquare,
  Calculator,
  FileText,
  LayoutDashboard,
  Wallet,
  Lock,
  ArrowRight,
  Loader2,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Search,
  Download,
  DollarSign,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Stats {
  totalSignups: number
  feedbackCount: number
  feedbackRate: number
  featureVotes: {
    acb: number
    tax: number
    dashboard: number
    wallet: number
  }
  exchangeBreakdown: {
    newton: number
    shakepay: number
    both: number
    other: number
  }
  pricingResponses: string[]  // Array of all pricing values entered by users
  pricingStats: {
    count: number      // How many users responded
    average: number    // Average amount
    min: number        // Minimum amount
    max: number        // Maximum amount
  }
}

interface Entry {
  id: number
  email: string
  exchange: string
  exchanges: string[]
  otherExchange?: string
  pricingPreference?: string
  featureText?: string
  votedFeatures: string[]
  createdAt: string
  updatedAt: string
  feedbackAt?: string
  editCount: number
  hasGivenFeedback: boolean
}

interface AdminData {
  stats: Stats
  entries: Entry[]
}

const FEATURE_INFO = {
  acb: { label: 'ACB + Superficial Loss', icon: Calculator, color: 'text-emerald-400' },
  tax: { label: 'CRA Tax Reports', icon: FileText, color: 'text-amber-400' },
  dashboard: { label: 'Real-time Dashboard', icon: LayoutDashboard, color: 'text-violet-400' },
  wallet: { label: 'Custom Wallet', icon: Wallet, color: 'text-sky-400' },
}

// FIX #10: Sanitize cell content to prevent CSV injection
const sanitizeCSVCell = (cell: string): string => {
  // Escape quotes by doubling them
  let sanitized = cell.replace(/"/g, '""')
  // Prefix with single quote if cell starts with dangerous characters
  // This prevents formula execution in Excel/Google Sheets
  if (/^[=+\-@\t\r]/.test(sanitized)) {
    sanitized = "'" + sanitized
  }
  return sanitized
}

export function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState('')
  const [data, setData] = useState<AdminData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  // FIX #3: Store password in memory only (not sessionStorage)
  const [currentPassword, setCurrentPassword] = useState<string | null>(null)

  const fetchData = async (pwd: string) => {
    setIsLoading(true)
    try {
      // FIX #2: Use Authorization header only (never URL params)
      const response = await fetch('/api/admin', {
        headers: {
          'Authorization': `Bearer ${pwd}`,
        },
      })
      const result = await response.json()

      if (response.status === 401) {
        setAuthError('Invalid password')
        setIsAuthenticated(false)
        setCurrentPassword(null)
        return
      }

      if (response.status === 429) {
        setAuthError(result.error || 'Too many login attempts. Please try again later.')
        setIsAuthenticated(false)
        setCurrentPassword(null)
        return
      }

      if (!response.ok) {
        throw new Error(result.error)
      }

      setData(result)
      setIsAuthenticated(true)
      setLastRefresh(new Date())
      // FIX #3: Store password in component state only (cleared on page refresh)
      setCurrentPassword(pwd)
    } catch {
      setAuthError('Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  // FIX #3: Remove sessionStorage check - require login each session
  // This is more secure as password is never persisted

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError('')
    fetchData(password)
  }

  const handleRefresh = () => {
    if (currentPassword) {
      fetchData(currentPassword)
    }
  }

  const exportCSV = () => {
    if (!data) return

    const headers = ['Email', 'Exchange', 'Exchanges (All)', 'Other Exchange', 'Pricing Preference', 'Feature Text', 'Voted Features', 'Created', 'Feedback Given']
    // FIX #10: Use sanitizeCSVCell to prevent CSV injection attacks
    const rows = data.entries.map((e) => [
      sanitizeCSVCell(e.email),
      sanitizeCSVCell(e.exchange),
      sanitizeCSVCell(e.exchanges.join(';')),
      sanitizeCSVCell(e.otherExchange || ''),
      sanitizeCSVCell(e.pricingPreference || ''),
      sanitizeCSVCell(e.featureText || ''),
      sanitizeCSVCell(e.votedFeatures.join(';')),
      sanitizeCSVCell(e.createdAt),
      e.hasGivenFeedback ? 'Yes' : 'No',
    ])

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `trackercapital-waitlist-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Filter entries
  const filteredEntries = data?.entries.filter(
    (e) =>
      e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.featureText || '').toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="noise-overlay" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="p-8 bg-midnight-800/60 backdrop-blur-xl border border-slate-700/40 rounded-2xl">
            <div className="flex items-center justify-center w-14 h-14 mb-6 mx-auto bg-gold-500/10 rounded-xl">
              <Lock className="w-7 h-7 text-gold-400" />
            </div>

            <h1 className="text-2xl font-bold text-slate-100 text-center mb-2">Admin Dashboard</h1>
            <p className="text-slate-400 text-center mb-8">Enter password to access</p>

            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className={cn(
                  'w-full px-5 py-4 bg-midnight-900/60 border border-slate-700/50 rounded-xl',
                  'text-slate-100 placeholder-slate-500',
                  'focus:outline-none focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20',
                  authError && 'border-loss-500/50'
                )}
              />

              {authError && (
                <p className="text-loss-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {authError}
                </p>
              )}

              <button
                type="submit"
                disabled={isLoading || !password}
                className={cn(
                  'w-full px-6 py-4 font-semibold rounded-xl transition-all',
                  'bg-gold-500 text-midnight-950',
                  'hover:bg-gold-400',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2'
                )}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Access Dashboard
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    )
  }

  // Dashboard
  return (
    <div className="min-h-screen pb-12">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-midnight-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-slate-100">TrackerCapital Admin</h1>
            {lastRefresh && (
              <span className="text-xs text-slate-500">
                Updated {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 text-slate-400 hover:text-gold-400 transition-colors"
            >
              <RefreshCw className={cn('w-5 h-5', isLoading && 'animate-spin')} />
            </button>

            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-200 rounded-lg hover:bg-slate-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 pt-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Total Signups */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 bg-midnight-800/40 border border-slate-700/40 rounded-xl"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-gold-500/10 rounded-lg">
                <Users className="w-5 h-5 text-gold-400" />
              </div>
              <span className="text-slate-400 text-sm">Total Signups</span>
            </div>
            <p className="text-3xl font-bold text-slate-100 font-mono">
              {data?.stats.totalSignups.toLocaleString()}
            </p>
          </motion.div>

          {/* Feedback Given */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-6 bg-midnight-800/40 border border-slate-700/40 rounded-xl"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-profit-500/10 rounded-lg">
                <MessageSquare className="w-5 h-5 text-profit-400" />
              </div>
              <span className="text-slate-400 text-sm">Gave Feedback</span>
            </div>
            <p className="text-3xl font-bold text-slate-100 font-mono">
              {data?.stats.feedbackCount}
              <span className="text-lg text-slate-500 ml-2">
                ({data?.stats.feedbackRate}%)
              </span>
            </p>
          </motion.div>

          {/* Top Feature */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 bg-midnight-800/40 border border-slate-700/40 rounded-xl"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <TrendingUp className="w-5 h-5 text-amber-400" />
              </div>
              <span className="text-slate-400 text-sm">Top Feature</span>
            </div>
            {data && (
              <p className="text-lg font-semibold text-slate-100">
                {Object.entries(data.stats.featureVotes).sort(([, a], [, b]) => b - a)[0]?.[0] === 'acb'
                  ? 'ACB Tracking'
                  : Object.entries(data.stats.featureVotes).sort(([, a], [, b]) => b - a)[0]?.[0] === 'tax'
                    ? 'Tax Reports'
                    : 'Dashboard'}
              </p>
            )}
          </motion.div>

          {/* Exchange Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 bg-midnight-800/40 border border-slate-700/40 rounded-xl"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-violet-500/10 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-violet-400" />
              </div>
              <span className="text-slate-400 text-sm">Using Both</span>
            </div>
            <p className="text-3xl font-bold text-slate-100 font-mono">
              {data?.stats.exchangeBreakdown.both}
            </p>
          </motion.div>
        </div>

        {/* Feature Votes Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8 p-6 bg-midnight-800/40 border border-slate-700/40 rounded-xl"
        >
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Feature Votes</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {data &&
              Object.entries(FEATURE_INFO).map(([key, info]) => {
                const votes = data.stats.featureVotes[key as keyof typeof data.stats.featureVotes]
                const Icon = info.icon
                const percentage = data.stats.feedbackCount > 0
                  ? Math.round((votes / data.stats.feedbackCount) * 100)
                  : 0

                return (
                  <div key={key} className="flex items-center gap-4">
                    <div className={cn('p-3 rounded-xl bg-slate-800/50')}>
                      <Icon className={cn('w-6 h-6', info.color)} />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-200 font-medium">{info.label}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={cn('h-full rounded-full', {
                              'bg-emerald-500': key === 'acb',
                              'bg-amber-500': key === 'tax',
                              'bg-violet-500': key === 'dashboard',
                              'bg-sky-500': key === 'wallet',
                            })}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-slate-400 text-sm font-mono w-16">
                          {votes} ({percentage}%)
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>
        </motion.div>

        {/* Pricing Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-8 p-6 bg-midnight-800/40 border border-slate-700/40 rounded-xl"
        >
          <h2 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gold-400" />
            Pricing Insights
          </h2>
          {data && data.stats.pricingStats.count > 0 ? (
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm mb-1">Responses</p>
                <p className="text-2xl font-bold text-slate-100">{data.stats.pricingStats.count}</p>
              </div>
              <div className="text-center p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm mb-1">Average</p>
                <p className="text-2xl font-bold text-gold-400">${data.stats.pricingStats.average.toFixed(2)}</p>
              </div>
              <div className="text-center p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm mb-1">Min</p>
                <p className="text-2xl font-bold text-emerald-400">${data.stats.pricingStats.min.toFixed(2)}</p>
              </div>
              <div className="text-center p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm mb-1">Max</p>
                <p className="text-2xl font-bold text-violet-400">${data.stats.pricingStats.max.toFixed(2)}</p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-4">No pricing responses yet</p>
          )}
          {data && data.stats.pricingResponses.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-slate-400 text-sm mb-2">All responses:</p>
              <div className="flex flex-wrap gap-2">
                {data.stats.pricingResponses.map((price, i) => (
                  <span key={i} className="px-2 py-1 bg-slate-800/50 rounded text-slate-300 text-sm">
                    ${price}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by email or feedback..."
              className="w-full pl-12 pr-4 py-3 bg-midnight-800/40 border border-slate-700/40 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:border-gold-500/50"
            />
          </div>
        </div>

        {/* Entries Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-midnight-800/40 border border-slate-700/40 rounded-xl overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">#</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Email</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Exchange</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Pricing</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Votes</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Feature Request</th>
                  <th className="text-left px-6 py-4 text-slate-400 font-medium text-sm">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredEntries?.map((entry, index) => (
                  <tr
                    key={entry.id}
                    className="border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors"
                  >
                    <td className="px-6 py-4 text-slate-500 font-mono text-sm">{index + 1}</td>
                    <td className="px-6 py-4">
                      <span className="text-slate-200">{entry.email}</span>
                      {entry.hasGivenFeedback && (
                        <CheckCircle2 className="w-4 h-4 text-profit-400 inline ml-2" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-400 capitalize">
                      {entry.exchange}
                      {entry.otherExchange && (
                        <span className="text-slate-500 text-sm ml-1">({entry.otherExchange})</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {entry.pricingPreference ? (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-gold-500/20 text-gold-400">
                          ${entry.pricingPreference}/mo
                        </span>
                      ) : (
                        <span className="text-slate-600 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        {entry.votedFeatures.map((v) => {
                          const info = FEATURE_INFO[v as keyof typeof FEATURE_INFO]
                          if (!info) return null
                          const Icon = info.icon
                          return (
                            <div
                              key={v}
                              className="p-1.5 bg-slate-800/50 rounded"
                              title={info.label}
                            >
                              <Icon className={cn('w-4 h-4', info.color)} />
                            </div>
                          )
                        })}
                        {entry.votedFeatures.length === 0 && (
                          <span className="text-slate-600 text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      {entry.featureText ? (
                        <p className="text-slate-300 text-sm truncate" title={entry.featureText}>
                          {entry.featureText}
                        </p>
                      ) : (
                        <span className="text-slate-600 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-sm whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredEntries?.length === 0 && (
            <div className="p-12 text-center text-slate-500">No entries found</div>
          )}
        </motion.div>
      </main>
    </div>
  )
}
